/* eslint-disable no-console */
/* eslint-disable no-nested-ternary */
import axios from 'axios';
import toast from 'react-hot-toast';
import { Product, Collection, JewelryType } from '../../entities';
import Customer from '../../entities/customer';
import CustomerResponse from '../../entities/customerResponse';
import Gem from '../../entities/gem';
import Metal from '../../entities/metal';
import Order from '../../entities/order';
import ProductResponse from '../../entities/productResponse';

export const TOKEN_KEY = '@galateausa-Token';
export const GUEST_KEY = '@galateausa-Guest';
export const CHAT_STATUS = '@galateausa-ChatStatus';
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const isAuthenticated = () => localStorage.getItem(TOKEN_KEY) !== null;
export const isGuest = () =>
  localStorage.getItem(GUEST_KEY) !== null || !localStorage.getItem(TOKEN_KEY);
export const getUser = () => JSON.parse(localStorage.getItem('userData'));
export const getCustomer = () =>
  JSON.parse(localStorage.getItem('customerData'));
export const getChatStatus = () =>
  JSON.parse(localStorage.getItem(CHAT_STATUS));

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'content-type': 'application/json',
  },
});

api.interceptors.request.use(async (config) => {
  const token = getToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.clear();
      window.location.replace('/#/auth/signin');
    }

    return Promise.reject(error);
  },
);

const handleError = (error) => {
  if (error) {
    toast.error(error, { duration: 5000 });
  }
};

export default {
  getCollections: async () => {
    let collections = [];
    try {
      const response = await api.get(
        'collections?pagination[pageSize]=100&populate=image,products,products.brand,products.jewelry_type,products.categories,products.collection,products.variations.metal,products.variations.gem,products.variations.chain_length,products.variations.chain_type,products.variations.ring_sizes,products.variations.image',
      );
      collections = response.data.data.map((data) => new Collection(data));
      collections = collections.sort((a, b) =>
        a.title > b.title ? 1 : b.title > a.title ? -1 : 0,
      );
    } catch (e) {
      handleError(`Error parsing ${e}`);
    }
    return collections;
  },

  getCollectionById: async (collectionId) => {
    let collection = {};
    try {
      const response = await api.get(
        `/collections/${collectionId}?sort=[products.variations.sku]&populate=video,image,products,products.brand,products.jewelry_type,products.categories,products.collection,products.variations.metal,products.variations.gem,products.variations.chain_length,products.variations.chain_type,products.variations.ring_sizes,products.variations.image`,
      );
      collection = new Collection(response.data.data);
    } catch (e) {
      handleError(`Error fetching/parsing collections: ${e}`);
    }
    return collection;
  },

  getJewelryTypes: async () => {
    let jewelryTypes = [];
    try {
      const response = await api.get('/jewelry-types?populate=*');
      jewelryTypes = response.data.data.map((data) => new JewelryType(data));
      jewelryTypes = jewelryTypes.sort((a, b) =>
        a.title > b.title ? 1 : b.title > a.title ? -1 : 0,
      );
    } catch (e) {
      handleError(`Error fetching/parsing jewelryTypes: ${e}`);
    }
    return jewelryTypes;
  },

  getGems: async () => {
    let gems = [];
    try {
      const response = await api.get('/gems?populate=*');
      gems = response.data.data.map((data) => new Gem(data));
      gems = gems.sort((a, b) =>
        a.title > b.title ? 1 : b.title > a.title ? -1 : 0,
      );
    } catch (e) {
      handleError(`Error fetching/parsing gems: ${e}`);
    }
    return gems;
  },

  getMetals: async () => {
    let metals = [];
    try {
      const response = await api.get('/metals?populate=*');
      metals = response.data.data.map((data) => new Metal(data));
      metals = metals.sort((a, b) =>
        a.title > b.title ? 1 : b.title > a.title ? -1 : 0,
      );
    } catch (e) {
      handleError(`Error fetching/parsing metals: ${e}`);
    }
    return metals;
  },

  getSearchProducts: async (qs) => {
    let products = [];
    try {
      const { data } = await api.get(`/search?${qs}`);
      console.log(data);
      products =
        !!data && data.length > 0
          ? data.map((d) => new ProductResponse(d))
          : [];
    } catch (e) {
      console.log(e?.response?.data?.message);
      handleError(`Error fetching/parsing search products: ${e}`);
    }
    return products;
  },

  getProducts: async ({ page, pageSize }) => {
    let productsResp = [];
    let paginationResp;
    try {
      const response = await api.get(
        `/products?sort[0]=code%3Aasc&pagination[page]=${page}&pagination[pageSize]=${pageSize}&populate=brand,jewelry_type,collection,variations.metal,variations.gem,variations.chain_length,variations.chain_type,variations.ring_size,variations.image`,
      );
      productsResp = response.data.data
        .map((data) => new Product(data))
        .filter((n) => Object.keys(n).length > 0);
      paginationResp = response.data.meta.pagination;
    } catch (e) {
      handleError(`Error fetching/parsing products: ${e}`);
      return { productsResp, paginationResp };
    }
    return { productsResp, paginationResp };
  },

  getProductById: async (id) => {
    let product = null;
    try {
      const response = await api.get(
        `/products/${id}?populate=brand,jewelry_type,categories,collection,variations.metal,variations.gem,variations.chain_length,variations.chain_type,variations.ring_sizes,variations.image`,
      );
      product = new Product(response.data.data);
    } catch (e) {
      handleError(`Error fetching/parsing productById: ${e}`);
    }
    return product;
  },

  postPlaceOrder: async (order) => {
    let result;
    try {
      const response = await api.post(`/orders/placeOrder`, { data: order });
      result = response;
    } catch (e) {
      handleError(`Error fetching/parsing order: ${e}`);
    }
    return result;
  },

  signUp: async (data) => {
    try {
      const resp = await api.post(`/auth/b2b/register`, data);
      return resp;
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return e?.response?.data?.error?.status;
    }
  },

  signIn: async ({ email, password }) => {
    try {
      const { data: userResp } = await api.post(`/auth/b2b`, {
        identifier: email,
        password,
      });

      const { jwt, user, orders, preferences, role } = userResp;
      const userData = {
        ...user,
        orders,
        role,
      };

      if (preferences) {
        preferences.forEach((p) => {
          if (p.key === 'CHAT_STATUS') {
            localStorage.setItem(CHAT_STATUS, p.value);
          }
        });
      }
      localStorage.setItem(TOKEN_KEY, jwt);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.removeItem(GUEST_KEY);

      if (role === 'Dealer') {
        const { data: customerResp } = await api.post(
          `/customers/findByEmail`,
          {
            email: user.email,
          },
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          },
        );

        if (customerResp) {
          const customerData = new CustomerResponse(customerResp);
          localStorage.setItem('customerData', JSON.stringify(customerData));
        }
      }

      return userResp.status;
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return e?.response?.data?.error?.status;
    }
  },
  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(GUEST_KEY);
    localStorage.removeItem('userData');
    localStorage.removeItem('customerData');
    localStorage.removeItem('cart');
  },

  passwordReset: async ({ email }) => {
    try {
      const response = await api.post(`/auth/forgot-password`, {
        email,
      });
      return response.status;
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return e?.response?.data?.error?.status;
    }
  },

  updateCustomerAccount: async (customerId, data) => {
    try {
      const response = await api.put(`/customers/${customerId}`, data);
      const responseCustomer = await api.get(
        `/customers/${customerId}?populate=ship_to`,
      );
      const customerData = new Customer(responseCustomer.data.data);
      localStorage.setItem('customerData', JSON.stringify(customerData));
      return response.status;
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return e?.response?.data?.error?.status;
    }
  },

  changeUserPassword: async (data) => {
    try {
      const response = await api.post(`/auth/reset-password`, data);
      const { jwt, user, role } = response.data;
      const userData = {
        ...user,
        role,
      };
      localStorage.setItem(TOKEN_KEY, jwt);
      localStorage.setItem('userData', JSON.stringify(userData));
      return response.status;
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return e?.response?.data?.error?.status;
    }
  },

  refreshUserOrders: async () => {
    try {
      const { data } = await api.get('/users/me');
      const userData = JSON.parse(localStorage.getItem('userData'));
      localStorage.setItem(
        'userData',
        JSON.stringify({ ...userData, orders: data.orders }),
      );
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
    }
  },

  fetchCustomerOrders: async (customerId) => {
    try {
      const response = await api.get(
        `/customers/${customerId}?sort=[id:desc]&populate=ship_to,orders,orders.order_items,orders.users_permissions_user`,
      );
      const customerData = new Customer(response?.data?.data);
      return customerData?.orders.sort((a, b) => b.id - a.id);
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return e?.response?.data?.error?.status;
    }
  },

  fetchOrderDetails: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}?populate=*`);
      const orderData = new Order(response.data.data);
      const customerData = new Customer(
        response?.data?.data?.attributes?.customer?.data,
      );
      return { order: orderData, customer: customerData };
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return { order: [], customer: {}, status: e.response.status };
    }
  },

  fetchUserWhitelist: async ({ email, token }) => {
    try {
      const response = await api.post('auth/b2b/white-list', { email, token });
      return response;
    } catch (e) {
      handleError('Invalid link');
      return { data: undefined, status: e.response.status };
    }
  },

  setUserPreferences: async ({ email, key, value }) => {
    try {
      const response = await api.post('/user-preferences', {
        email,
        key,
        value,
      });
      return response;
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return undefined;
    }
  },

  resendOrderMail: async ({ email, orderId }) => {
    try {
      const res = await api.post('/orders/resend-mail-order', {
        email,
        orderId,
      });
      return res;
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return e.response.status;
    }
  },

  listAllCustomers: async () => {
    try {
      const { data } = await api.get('/customers/listAllCustomers');
      return data;
    } catch (e) {
      handleError(e?.response?.data?.error?.message);
      return [];
    }
  },

  createOrUpdateCustomers: async (customer) => {
    try {
      const response = await api.post('/customers', customer);
      return response.status;
    } catch (e) {
      console.log(e?.response?.data?.error?.message);
      handleError(e?.response?.data?.error?.message);
      return [];
    }
  },
};
